import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Clock,
  Car,
  Receipt,
  BarChart3,
  Users,
  Zap,
  Shield,
  Globe,
  ChevronRight,
  Check,
  Star,
  ArrowRight,
  Play,
  Menu,
  X
} from 'lucide-react';
import MarketingLayout from '@/components/MarketingLayout';

export default function LandingPage() {
  const { t } = useTranslation();

  const features = [
    {
      icon: Clock,
      title: t('landing.features.timeTracking.title'),
      description: t('landing.features.timeTracking.description'),
      color: 'from-cyan-500 to-teal-500'
    },
    {
      icon: Car,
      title: t('landing.features.mileage.title'),
      description: t('landing.features.mileage.description'),
      color: 'from-teal-500 to-emerald-500'
    },
    {
      icon: Receipt,
      title: t('landing.features.invoicing.title'),
      description: t('landing.features.invoicing.description'),
      color: 'from-emerald-500 to-green-500'
    },
    {
      icon: BarChart3,
      title: t('landing.features.reports.title'),
      description: t('landing.features.reports.description'),
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Users,
      title: t('landing.features.clients.title'),
      description: t('landing.features.clients.description'),
      color: 'from-indigo-500 to-blue-500'
    },
    {
      icon: Shield,
      title: t('landing.features.security.title'),
      description: t('landing.features.security.description'),
      color: 'from-violet-500 to-indigo-500'
    }
  ];

  const testimonials = [
    {
      name: 'Mark de Vries',
      role: 'Freelance Developer',
      content: t('landing.testimonials.mark'),
      avatar: 'MD',
      rating: 5
    },
    {
      name: 'Lisa Jansen',
      role: 'Grafisch Ontwerper',
      content: t('landing.testimonials.lisa'),
      avatar: 'LJ',
      rating: 5
    },
    {
      name: 'Thomas Bakker',
      role: 'Consultant',
      content: t('landing.testimonials.thomas'),
      avatar: 'TB',
      rating: 5
    }
  ];

  const stats = [
    { value: '10.000+', label: t('landing.stats.users') },
    { value: '500.000+', label: t('landing.stats.hours') },
    { value: '€2M+', label: t('landing.stats.invoiced') },
    { value: '4.9/5', label: t('landing.stats.rating') }
  ];

  return (
    <MarketingLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20 pb-32">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute top-40 -left-40 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 mb-8">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-300">{t('landing.hero.badge')}</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t('landing.hero.title.part1')}{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
                {t('landing.hero.title.highlight')}
              </span>{' '}
              {t('landing.hero.title.part2')}
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              {t('landing.hero.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/25"
              >
                <Link to="/register" data-testid="hero-cta-register">
                  {t('landing.hero.cta.primary')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg rounded-xl"
              >
                <Link to="/functies" data-testid="hero-cta-features">
                  <Play className="w-5 h-5 mr-2" />
                  {t('landing.hero.cta.secondary')}
                </Link>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-slate-400">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-teal-400" />
                <span>{t('landing.hero.trust.free')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-teal-400" />
                <span>{t('landing.hero.trust.noCard')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-teal-400" />
                <span>{t('landing.hero.trust.cancel')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-slate-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50" id="features">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.features.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('landing.features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden"
              >
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            >
              <Link to="/functies">
                {t('landing.features.viewAll')}
                <ChevronRight className="w-5 h-5 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-lg text-slate-600">
              {t('landing.howItWorks.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[1, 2, 3].map((step) => (
              <div key={step} className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  {step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {t(`landing.howItWorks.step${step}.title`)}
                </h3>
                <p className="text-slate-600">
                  {t(`landing.howItWorks.step${step}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('landing.testimonials.title')}
            </h2>
            <p className="text-lg text-slate-300">
              {t('landing.testimonials.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 backdrop-blur">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-semibold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-slate-400">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-500 to-teal-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t('landing.cta.title')}
          </h2>
          <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
            {t('landing.cta.subtitle')}
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-cyan-600 hover:bg-cyan-50 px-10 py-6 text-lg rounded-xl shadow-xl"
          >
            <Link to="/register">
              {t('landing.cta.button')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </MarketingLayout>
  );
}
